import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEscapeKey } from "../../../hooks/useEscapeKey";
import { AnimatePresence, motion } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
  ArcElement,
} from "chart.js";
import { Doughnut, Line } from "react-chartjs-2";
import CloseIcon from "@mui/icons-material/Close";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import CircularProgress from "@mui/material/CircularProgress";
import DropDownListMultipleCustomized from "../../ui/kit-mdtw/DropDownListMultipleCustomized";
import { IOptionDropDownList } from "../../../interfaces/IDropDownList";
import {
  getApplicationStats,
  getRecruitmentSources,
  getClientsWithForms,
  IClientOption,
  IRecruitmentSourceOption,
  IRecruitmentSourceStat,
  IStatPoint,
  IStatSeries,
  StatsBreakdown,
  StatsGranularity,
  STATS_LIMITS,
} from "../../../services/applicationStatsService";
import { usePolling } from "../../../hooks/usePolling";
import AnimatedNumber from "../../ui/AnimatedNumber";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  ChartTooltip,
  Legend,
  Filler,
  ArcElement,
);

const MODAL_FONT = "'Inter', 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const SOURCE_COLORS = ["#0065DD", "#7c3aed", "#059669", "#f59e0b", "#e11d48", "#0891b2", "#65a30d", "#ea580c", "#64748b", "#db2777", "#4f46e5", "#0d9488"];

// Comparación de varias fuentes / clientes en el gráfico de líneas. Paleta
// categórica validada para daltonismo entre series adyacentes (orden fijo, no
// se cicla): con más de COMPARE_MAX_SERIES, las menores se agrupan en "Otros".
const COMPARE_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#6250d6", "#e34948"];
const COMPARE_MAX_SERIES = COMPARE_COLORS.length;
const COMPARE_OTHERS_KEY = "__otros__";
const COMPARE_OTHERS_COLOR = "#94a3b8";
const COMPARE_TOTAL_KEY = "__total__";
const COMPARE_TOTAL_COLOR = "#475569";
// Con pocas series se rotula cada línea en su extremo derecho.
const COMPARE_DIRECT_LABEL_MAX = 4;

const BREAKDOWN_LABEL: Record<StatsBreakdown, { singular: string; plural: string }> = {
  recruitmentSource: { singular: "Fuente", plural: "fuentes" },
  client:            { singular: "Cliente", plural: "clientes" },
};

interface ICompareSeries {
  key: string;
  label: string;
  total: number;
  color: string;
  data: number[];
}

const GRANULARITIES: { value: StatsGranularity; label: string }[] = [
  { value: "minutes", label: "Minutos" },
  { value: "hours",   label: "Horas"   },
  { value: "days",    label: "Días"    },
  { value: "months",  label: "Meses"   },
  { value: "years",   label: "Años"    },
];

// Tipo de <input> nativo por granularidad para el rango Desde/Hasta.
const RANGE_INPUT_TYPE: Record<StatsGranularity, string> = {
  minutes: "datetime-local",
  hours:   "datetime-local",
  days:    "date",
  months:  "month",
  years:   "number",
};

// Convierte el valor crudo del <input> al MISMO formato que usa el backend para
// agrupar/filtrar esa granularidad (comparación lexicográfica exacta con los
// buckets). Ver applicationStatsService / applicationQueries.
const toRangeBound = (granularity: StatsGranularity, raw: string): string => {
  if (!raw) return "";
  switch (granularity) {
    case "minutes": return raw.replace("T", " ").slice(0, 16); // YYYY-MM-DD HH:MM
    case "hours":   return raw.replace("T", " ").slice(0, 13); // YYYY-MM-DD HH
    case "days":    return raw.slice(0, 10);                    // YYYY-MM-DD
    case "months":  return raw.slice(0, 7);                     // YYYY-MM
    case "years":   return String(raw).slice(0, 4);             // YYYY
    default:        return raw;
  }
};

// Etiqueta del eje X por granularidad. Para horas el backend entrega
// "YYYY-MM-DD HH" → se muestra como "HH:00" (ej. 15:00). El resto queda tal cual
// (la fecha completa sigue disponible en el tooltip).
const formatAxisLabel = (granularity: StatsGranularity, raw: string): string => {
  if (granularity !== "hours") return raw;
  const hour = String(raw).split(" ")[1];
  return hour ? `${hour}:00` : raw;
};

const WEEKDAYS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];

// Nombre del día usando UTC para evitar corrimientos por zona horaria del navegador.
const weekdayName = (year: number, month: number, day: number): string =>
  WEEKDAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];

// Título del tooltip. Muestra nombre del día + fecha DD-MM-YYYY, agregando la
// hora según la granularidad (el backend entrega YYYY-MM-DD[ HH[:MI]] / YYYY-MM / YYYY):
//   días    → "miércoles 15-07-2026"
//   horas   → "miércoles 15-07-2026 13:00"
//   minutos → "miércoles 15-07-2026 14:30"
//   meses   → "07-2026"   ·   años → "2026"
const formatTooltipTitle = (granularity: StatsGranularity, raw: string): string => {
  const [datePart, timePart] = String(raw).split(" ");
  const seg = datePart.split("-");
  if (seg.length === 1) return seg[0];                       // años
  if (seg.length === 2) return `${seg[1]}-${seg[0]}`;        // meses → MM-YYYY
  const [y, m, d] = seg;
  const base = `${weekdayName(Number(y), Number(m), Number(d))} ${d}-${m}-${y}`;
  if (granularity === "hours")   return timePart ? `${base} ${timePart}:00` : base;
  if (granularity === "minutes") return timePart ? `${base} ${timePart}` : base;
  return base;                                               // días
};

// Plugin: línea vertical punteada que cruza el gráfico en el punto bajo el
// cursor, para leer con precisión el valor del eje Y.
// Plugin: rotula cada serie visible en su extremo derecho (nombre en tinta, el
// color lo aporta el punto), separando etiquetas que caerían encimadas.
const compareEndLabels = {
  id: "compareEndLabels",
  afterDatasetsDraw: (chart: any, _args: any, options: any) => {
    if (!options?.enabled) return;
    const { ctx, chartArea } = chart;
    const labels: { y: number; text: string; color: string }[] = [];
    chart.data.datasets.forEach((dataset: any, index: number) => {
      const meta = chart.getDatasetMeta(index);
      if (dataset.key === COMPARE_TOTAL_KEY || !chart.isDatasetVisible(index) || !meta.data.length) return;
      const last = meta.data[meta.data.length - 1];
      const name = String(dataset.label ?? "");
      labels.push({
        y: last.y,
        text: name.length > 18 ? `${name.slice(0, 17)}…` : name,
        color: dataset.borderColor,
      });
    });
    labels.sort((a, b) => a.y - b.y);
    const GAP = 13;
    for (let i = 1; i < labels.length; i += 1) {
      if (labels[i].y - labels[i - 1].y < GAP) labels[i].y = labels[i - 1].y + GAP;
    }
    ctx.save();
    ctx.font = `600 10.5px ${MODAL_FONT}`;
    ctx.textBaseline = "middle";
    labels.forEach(({ y, text, color }) => {
      const x = chartArea.right + 8;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x + 3, y, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#475569";
      ctx.fillText(text, x + 10, y);
    });
    ctx.restore();
  },
};

const verticalHoverLine = {
  id: "verticalHoverLine",
  afterDraw: (chart: any) => {
    const active = chart.tooltip?.getActiveElements?.() ?? [];
    if (!active.length) return;
    const { ctx, chartArea } = chart;
    const x = active[0].element.x;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = "rgba(0,101,221,0.45)";
    ctx.stroke();
    ctx.restore();
  },
};

// Unidad en plural por granularidad, para etiquetas legibles ("Máx. días",
// "mostrando los últimos 48 horas", etc.).
const UNIT_LABEL: Record<StatsGranularity, string> = {
  minutes: "minutos",
  hours:   "horas",
  days:    "días",
  months:  "meses",
  years:   "años",
};

const RANGE_HINT: Record<StatsGranularity, string> = {
  minutes: "Filtra por fecha y hora (minuto)",
  hours:   "Filtra por fecha y hora",
  days:    "Filtra por día",
  months:  "Filtra por mes",
  years:   "Filtra por año",
};

interface EstadisticasModalProps {
  // Cuando viene un formulario, las estadísticas son exclusivas de ese
  // formulario; cuando es null/undefined, son generales (todos los formularios).
  contextForm?: { contextFormId: string; code?: string } | null;
  onClose: () => void;
}

const EstadisticasModal = ({ contextForm, onClose }: EstadisticasModalProps) => {
  // Esc cierra el modal.
  useEscapeKey(onClose);
  const [granularity, setGranularity] = useState<StatsGranularity>("days");
  const [fromInput, setFromInput]     = useState<string>("");
  const [toInput, setToInput]         = useState<string>("");
  const [limit, setLimit]             = useState<number>(STATS_LIMITS.days.default);
  const [stats, setStats]             = useState<IStatPoint[]>([]);
  const [sourceDistribution, setSourceDistribution] = useState<IRecruitmentSourceStat[]>([]);
  const [recruitmentSources, setRecruitmentSources] = useState<IRecruitmentSourceOption[]>([]);
  const [recruitmentSourceFormVals, setRecruitmentSourceFormVals] = useState<any>({ recruitmentSource: null });
  const [clients, setClients] = useState<IClientOption[]>([]);
  const [clientFormVals, setClientFormVals] = useState<any>({ client: null });
  const [series, setSeries] = useState<IStatSeries[]>([]);
  // Dimensión a la que corresponden las `series` recibidas: evita dibujar las
  // de la consulta anterior mientras llega la nueva al cambiar lo comparado.
  const [seriesBreakdown, setSeriesBreakdown] = useState<StatsBreakdown | null>(null);
  // Dimensión a comparar cuando hay varias fuentes Y varios clientes elegidos.
  const [compareBy, setCompareBy] = useState<StatsBreakdown>("recruitmentSource");
  // Series apagadas desde la leyenda del gráfico comparativo.
  const [seriesOcultas, setSeriesOcultas] = useState<string[]>([]);
  const [uniqueApplicants, setUniqueApplicants] = useState<number>(0);
  const [loading, setLoading]         = useState<boolean>(true);
  const [error, setError]             = useState<string | null>(null);
  // Fuentes tachadas en la leyenda y si su exclusión debe rehacer los totales.
  const [fuentesOcultas, setFuentesOcultas] = useState<string[]>([]);
  const [recalcularDistribucion, setRecalcularDistribucion] = useState<boolean>(false);

  const contextFormId = contextForm?.contextFormId;
  const isPerForm     = !!contextFormId;
  const maxLimit      = STATS_LIMITS[granularity].max;

  const sourceOptions = useMemo<IOptionDropDownList[]>(
    () => recruitmentSources.map((source) => ({ name: source.name, code: source.recruitmentSourceId })),
    [recruitmentSources],
  );
  const clientOptions = useMemo<IOptionDropDownList[]>(
    () => clients.map((client) => ({ name: client.name, code: client.clientId })),
    [clients],
  );

  // Ids elegidos, en el orden en que se seleccionaron (fija el color de cada
  // serie al compararlas). Con "Todos" marcado no se filtra: así entran también
  // las postulaciones sin fuente / sin cliente.
  const selectedSourceIds: string[] = (recruitmentSourceFormVals.recruitmentSource?.value ?? [])
    .map((option: IOptionDropDownList) => String(option.code));
  const selectedClientIds: string[] = (clientFormVals.client?.value ?? [])
    .map((option: IOptionDropDownList) => String(option.code));
  const allSourcesSelected = sourceOptions.length > 0 && selectedSourceIds.length === sourceOptions.length;
  const allClientsSelected = clientOptions.length > 0 && selectedClientIds.length === clientOptions.length;
  const sourceFilterKey = allSourcesSelected ? "" : selectedSourceIds.join(",");
  const clientFilterKey = allClientsSelected ? "" : selectedClientIds.join(",");

  // Con 2+ elementos en un filtro, el gráfico compara una línea por cada uno.
  // Si ambos filtros tienen varios, se compara por la dimensión elegida.
  const canCompareSources = selectedSourceIds.length >= 2;
  const canCompareClients = selectedClientIds.length >= 2;
  const breakdown: StatsBreakdown | undefined = isPerForm
    ? undefined
    : canCompareSources && canCompareClients
      ? compareBy
      : canCompareSources
        ? "recruitmentSource"
        : canCompareClients
          ? "client"
          : undefined;
  // Orden estable de los ids de la dimensión comparada (orden de selección).
  const compareOrderKey = breakdown === "recruitmentSource"
    ? selectedSourceIds.join(",")
    : breakdown === "client"
      ? selectedClientIds.join(",")
      : "";

  useEffect(() => {
    if (isPerForm) return;
    let cancelled = false;
    getRecruitmentSources()
      .then((sources) => { if (!cancelled) setRecruitmentSources(sources); })
      .catch(() => { if (!cancelled) setRecruitmentSources([]); });
    getClientsWithForms()
      .then((list) => { if (!cancelled) setClients(list); })
      .catch(() => { if (!cancelled) setClients([]); });
    return () => { cancelled = true; };
  }, [isPerForm]);

  // Cambiar de granularidad reinicia el rango y el límite (el formato del rango
  // y el default/máximo dependen de la granularidad).
  const handleGranularityChange = (g: StatsGranularity) => {
    setGranularity(g);
    setFromInput("");
    setToInput("");
    setLimit(STATS_LIMITS[g].default);
  };

  const clearRange = () => {
    setFromInput("");
    setToInput("");
  };

  // Guardas para descartar respuestas obsoletas (cambio de parámetros o un
  // polling en vuelo) y para no llamar a setState tras desmontar el modal.
  const statsRequestIdRef = useRef<number>(0);
  const mountedRef        = useRef<boolean>(true);
  // Se re-arma en cada montaje: en StrictMode (dev) el efecto corre
  // mount → unmount → mount, así que hay que volver a poner `true` o las
  // respuestas quedarían descartadas para siempre (spinner eterno).
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Carga de estadísticas. Con `silent: true` es un refresco en segundo plano:
  // no muestra el spinner ni vacía el gráfico, y conserva los últimos datos
  // válidos si la vuelta falla, para que la actualización pase desapercibida.
  const loadStats = useCallback(
    (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;

      // Normaliza el rango: si Desde > Hasta, se intercambian (comparación
      // lexicográfica válida porque ambos están en el mismo formato zero-padded).
      let from = toRangeBound(granularity, fromInput);
      let to   = toRangeBound(granularity, toInput);
      if (from && to && from > to) [from, to] = [to, from];

      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const requestId = ++statsRequestIdRef.current;

      return getApplicationStats({
        granularity,
        contextFormId,
        recruitmentSourceIds: !isPerForm && sourceFilterKey ? sourceFilterKey.split(",") : undefined,
        clientIds: !isPerForm && clientFilterKey ? clientFilterKey.split(",") : undefined,
        breakdown,
        from: from || undefined,
        to: to || undefined,
        limit,
      })
        .then((data) => {
          if (!mountedRef.current || requestId !== statsRequestIdRef.current) return;
          setStats(Array.isArray(data?.stats) ? data.stats : []);
          setSourceDistribution(Array.isArray(data?.sourceDistribution) ? data.sourceDistribution : []);
          setUniqueApplicants(Number(data?.uniqueApplicants) || 0);
          setSeries(Array.isArray(data?.series) ? data.series : []);
          setSeriesBreakdown(data?.breakdown ?? null);
          setError(null);
        })
        .catch(() => {
          if (!mountedRef.current || requestId !== statsRequestIdRef.current) return;
          // El polling silencioso conserva los últimos datos válidos: no vacía
          // el gráfico ni muestra error si una vuelta falla.
          if (silent) return;
          setStats([]);
          setSourceDistribution([]);
          setUniqueApplicants(0);
          setSeries([]);
          setSeriesBreakdown(null);
          setError("No se pudieron cargar las estadísticas.");
        })
        .finally(() => {
          if (!mountedRef.current || requestId !== statsRequestIdRef.current) return;
          if (!silent) setLoading(false);
        });
    },
    [granularity, contextFormId, isPerForm, sourceFilterKey, clientFilterKey, breakdown, fromInput, toInput, limit],
  );

  // Carga con debounce ante cambios de parámetros (granularidad, rango, límite,
  // fuente…). El límite y los inputs numéricos disparan muchos cambios seguidos.
  useEffect(() => {
    const timer = setTimeout(() => { loadStats(); }, 300);
    return () => {
      clearTimeout(timer);
      // Invalida cualquier petición en vuelo de los parámetros anteriores.
      statsRequestIdRef.current += 1;
    };
  }, [loadStats]);

  // Polling silencioso mientras el modal está abierto: refresca los datos sin
  // spinner ni parpadeo. Como el modal solo está montado cuando está abierto,
  // deja de consultar automáticamente al cerrarse.
  usePolling(() => { loadStats({ silent: true }); });

  const totalApplications = useMemo(
    () => stats.reduce((acc, p) => acc + (Number(p.total) || 0), 0),
    [stats],
  );
  const peak = useMemo(
    () => stats.reduce((max, p) => Math.max(max, Number(p.total) || 0), 0),
    [stats],
  );
  // El peak solo no dice nada sin una base con la que compararlo.
  const promedio = useMemo(
    () => (stats.length > 0 ? Math.round(totalApplications / stats.length) : 0),
    [stats.length, totalApplications],
  );
  const postulacionesPorPersona = useMemo(
    () => (uniqueApplicants > 0 ? totalApplications / uniqueApplicants : null),
    [totalApplications, uniqueApplicants],
  );

  // Series a comparar, alineadas a los buckets de la serie total (un bucket
  // sin postulaciones de esa fuente/cliente vale 0). Ordenadas por total desc.
  const compareSeries = useMemo<ICompareSeries[]>(() => {
    if (!breakdown || seriesBreakdown !== breakdown || series.length === 0) return [];
    const labels = stats.map((p) => p.date);
    const align = (points: IStatPoint[]): number[] => {
      const byDate = new Map(points.map((p) => [p.date, Number(p.total) || 0]));
      return labels.map((date) => byDate.get(date) ?? 0);
    };

    const ordered = [...series].sort((a, b) => b.total - a.total);
    const overflow = ordered.length > COMPARE_MAX_SERIES;
    const named = overflow ? ordered.slice(0, COMPARE_MAX_SERIES - 1) : ordered;
    const rest = overflow ? ordered.slice(COMPARE_MAX_SERIES - 1) : [];

    // El color sigue a la entidad, no a su ranking: cada serie toma el slot de
    // su posición en la selección (así agregar o quitar otra no la recolorea);
    // si ese slot no existe o ya está tomado, el primero libre.
    const palette = overflow ? COMPARE_COLORS.slice(0, COMPARE_MAX_SERIES - 1) : COMPARE_COLORS;
    const order = compareOrderKey ? compareOrderKey.split(",") : [];
    const slotByKey = new Map<string, number>();
    const usedSlots = new Set<number>();
    named.forEach((item) => {
      const preferred = order.indexOf(item.key);
      if (preferred >= 0 && preferred < palette.length && !usedSlots.has(preferred)) {
        slotByKey.set(item.key, preferred);
        usedSlots.add(preferred);
      }
    });
    named.forEach((item) => {
      if (slotByKey.has(item.key)) return;
      const free = palette.findIndex((_, slot) => !usedSlots.has(slot));
      slotByKey.set(item.key, free);
      usedSlots.add(free);
    });

    const result: ICompareSeries[] = named.map((item) => ({
      key: item.key,
      label: item.label,
      total: item.total,
      color: palette[slotByKey.get(item.key) ?? 0],
      data: align(item.points),
    }));
    if (rest.length > 0) {
      const restData = rest.map((item) => align(item.points));
      result.push({
        key: COMPARE_OTHERS_KEY,
        label: `Otros (${rest.length})`,
        total: rest.reduce((acc, item) => acc + item.total, 0),
        color: COMPARE_OTHERS_COLOR,
        data: labels.map((_, i) => restData.reduce((acc, values) => acc + values[i], 0)),
      });
    }
    return result;
  }, [breakdown, seriesBreakdown, series, stats, compareOrderKey]);

  const isComparing = compareSeries.length > 0;
  const compareSum = useMemo(
    () => compareSeries.reduce((acc, item) => acc + item.total, 0),
    [compareSeries],
  );
  const visibleCompareCount = compareSeries.filter((item) => !seriesOcultas.includes(item.key)).length;

  // Cambiar lo que se compara limpia las series apagadas de la leyenda.
  useEffect(() => { setSeriesOcultas([]); }, [breakdown]);

  const alternarSerie = (clave: string): void =>
    setSeriesOcultas((actuales) =>
      actuales.includes(clave)
        ? actuales.filter((item) => item !== clave)
        : [...actuales, clave],
    );

  const chartData = useMemo(() => {
    const labels = stats.map((p) => p.date);
    const totals = stats.map((p) => Number(p.total) || 0);

    // Comparación estilo Analytics: el total queda de fondo (línea punteada con
    // un área tenue) y cada fuente/cliente es una línea sólida de su color.
    if (isComparing) {
      return {
        labels,
        datasets: [
          {
            key: COMPARE_TOTAL_KEY,
            label: "Total",
            data: totals,
            borderColor: COMPARE_TOTAL_COLOR,
            borderDash: [4, 4],
            borderWidth: 1.5,
            backgroundColor: "rgba(42,120,214,0.08)",
            pointRadius: 0,
            pointHoverRadius: 4,
            pointBackgroundColor: COMPARE_TOTAL_COLOR,
            fill: "origin",
            tension: 0.3,
            order: 100,
            hidden: seriesOcultas.includes(COMPARE_TOTAL_KEY),
          },
          ...compareSeries.map((item) => ({
            key: item.key,
            label: item.label,
            data: item.data,
            borderColor: item.color,
            backgroundColor: item.color,
            borderWidth: 2,
            pointRadius: stats.length > 60 ? 0 : 2.5,
            pointHoverRadius: 5,
            pointBackgroundColor: item.color,
            pointBorderColor: "#fff",
            pointBorderWidth: 1,
            fill: false,
            tension: 0.3,
            hidden: seriesOcultas.includes(item.key),
          })),
        ],
      };
    }

    return {
      labels,
      datasets: [
        {
          label: "Postulaciones",
          data: totals,
          borderColor: "#0065DD",
          backgroundColor: (ctx: any) => {
            const { chart } = ctx;
            const { ctx: c, chartArea } = chart;
            if (!chartArea) return "rgba(0,101,221,0.12)";
            const gradient = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, "rgba(0,101,221,0.28)");
            gradient.addColorStop(1, "rgba(0,101,221,0.01)");
            return gradient;
          },
          borderWidth: 2,
          pointRadius: stats.length > 60 ? 0 : 3,
          pointHoverRadius: 5,
          pointBackgroundColor: "#0065DD",
          fill: true,
          tension: 0.3,
        },
      ],
    };
  }, [stats, isComparing, compareSeries, seriesOcultas]);

  const directLabels = isComparing && visibleCompareCount > 0 && visibleCompareCount <= COMPARE_DIRECT_LABEL_MAX;

  // Tooltip del modo comparación: el total arriba y luego cada serie de mayor a
  // menor, con su participación sobre el total de ese bucket.
  const compareTooltip = useMemo(() => ({
    displayColors: true,
    usePointStyle: true,
    boxWidth: 8,
    boxHeight: 8,
    backgroundColor: "#0f172a",
    padding: 12,
    titleFont: { family: MODAL_FONT, size: 12 },
    bodyFont: { family: MODAL_FONT, size: 12 },
    bodySpacing: 5,
    itemSort: (a: any, b: any) => {
      if (a.dataset?.key === COMPARE_TOTAL_KEY) return -1;
      if (b.dataset?.key === COMPARE_TOTAL_KEY) return 1;
      return (Number(b.parsed?.y) || 0) - (Number(a.parsed?.y) || 0);
    },
    callbacks: {
      title: (items: any[]): string => formatTooltipTitle(granularity, items[0]?.label ?? ""),
      label: (item: any): string => {
        const value = Number(item.parsed?.y) || 0;
        if (item.dataset?.key === COMPARE_TOTAL_KEY) return ` Total: ${value.toLocaleString("es-CL")}`;
        const totalDataset = item.chart?.data?.datasets?.find((d: any) => d.key === COMPARE_TOTAL_KEY);
        const bucketTotal = Number(totalDataset?.data?.[item.dataIndex]) || 0;
        const share = bucketTotal ? ` · ${((value / bucketTotal) * 100).toFixed(1).replace(".", ",")}%` : "";
        return ` ${item.dataset?.label}: ${value.toLocaleString("es-CL")}${share}`;
      },
      labelColor: (item: any) => ({
        borderColor: item.dataset?.borderColor,
        backgroundColor: item.dataset?.borderColor,
      }),
      labelTextColor: (item: any) => (item.dataset?.key === COMPARE_TOTAL_KEY ? "#ffffff" : "#e2e8f0"),
    },
  }), [granularity]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: isComparing ? compareTooltip : {
          displayColors: false,
          backgroundColor: "#0f172a",
          padding: 12,
          titleFont: { family: MODAL_FONT, size: 12 },
          bodyFont: { family: MODAL_FONT, size: 13, weight: "bold" as const },
          callbacks: {
            // Título: fecha + conteo (el conteo queda en el título para poder
            // colorear en el cuerpo SOLO la variación, estilo bolsa de valores).
            title: (items: any[]): string[] => [
              formatTooltipTitle(granularity, items[0]?.label ?? ""),
              `${items[0]?.formattedValue ?? ""} postulación(es)`,
            ],
            // Cuerpo: variación porcentual respecto al punto anterior de la serie.
            label: (item: any): string => {
              const data = (item.dataset?.data ?? []) as number[];
              const i = item.dataIndex;
              if (i <= 0) return "";
              const prev = Number(data[i - 1]);
              const curr = Number(data[i]);
              if (!prev) return "";
              const pct = ((curr - prev) / prev) * 100;
              const arrow = pct > 0 ? "▲" : pct < 0 ? "▼" : "▬";
              const sign = pct > 0 ? "+" : "";
              return `${arrow} ${sign}${pct.toFixed(1).replace(".", ",")}% vs. anterior`;
            },
            // Color de la variación: verde sube, rojo baja, gris sin cambio.
            labelTextColor: (item: any) => {
              const data = (item.dataset?.data ?? []) as number[];
              const i = item.dataIndex;
              if (i <= 0) return "#e2e8f0";
              const prev = Number(data[i - 1]);
              const curr = Number(data[i]);
              if (!prev) return "#e2e8f0";
              const diff = curr - prev;
              return diff > 0 ? "#22c55e" : diff < 0 ? "#ef4444" : "#94a3b8";
            },
          },
        },
        compareEndLabels: { enabled: directLabels },
      },
      layout: { padding: { right: directLabels ? 118 : 0 } },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: "#94a3b8",
            font: { family: MODAL_FONT, size: 10 },
            maxRotation: 60,
            autoSkip: true,
            maxTicksLimit: 14,
            callback: function (this: any, value: any): string {
              const raw = this.getLabelForValue(value);
              return formatAxisLabel(granularity, raw);
            },
          },
        },
        y: {
          beginAtZero: true,
          grid: { color: "rgba(148,163,184,0.15)" },
          ticks: {
            color: "#94a3b8",
            font: { family: MODAL_FONT, size: 11 },
            precision: 0,
          },
        },
      },
    }),
    [granularity, isComparing, directLabels, compareTooltip],
  );

  // Clave estable por fuente: se usa para tachar y para fijar el color, de modo
  // que ocultar una no recolorea al resto.
  const claveFuente = (item: IRecruitmentSourceStat): string =>
    item.recruitmentSourceId ?? item.recruitmentSource;

  const colorPorFuente = useMemo(() => {
    const mapa: Record<string, string> = {};
    sourceDistribution.forEach((item, index) => {
      mapa[claveFuente(item)] = SOURCE_COLORS[index % SOURCE_COLORS.length];
    });
    return mapa;
  }, [sourceDistribution]);

  const fuentesVisibles = useMemo(
    () => sourceDistribution.filter((item) => !fuentesOcultas.includes(claveFuente(item))),
    [sourceDistribution, fuentesOcultas],
  );

  const totalDistribucion = useMemo(
    () => sourceDistribution.reduce((acc, item) => acc + (Number(item.total) || 0), 0),
    [sourceDistribution],
  );
  const totalVisible = useMemo(
    () => fuentesVisibles.reduce((acc, item) => acc + (Number(item.total) || 0), 0),
    [fuentesVisibles],
  );

  // Con "recalcular" en ON las fuentes tachadas salen del total, así que los
  // porcentajes se reparten solo entre las visibles. En OFF el total sigue
  // siendo el del período completo y los porcentajes no cambian al tachar.
  const basePorcentaje = recalcularDistribucion ? totalVisible : totalDistribucion;

  const porcentajeDe = (valor: number): number =>
    basePorcentaje ? (valor / basePorcentaje) * 100 : 0;

  const formatearPorcentaje = (valor: number): string =>
    `${valor.toFixed(2).replace(".", ",")}%`;

  const alternarFuente = (clave: string): void =>
    setFuentesOcultas((actuales) =>
      actuales.includes(clave)
        ? actuales.filter((item) => item !== clave)
        : [...actuales, clave],
    );

  // Tooltip HTML externo del donut (evita que el nativo, dibujado dentro del
  // canvas de 280px, recorte el texto largo contra el borde).
  const doughnutTooltipRef = useRef<HTMLDivElement | null>(null);

  const sourceChartData = useMemo(() => ({
    labels: fuentesVisibles.map((item) => item.recruitmentSource),
    datasets: [{
      data: fuentesVisibles.map((item) => Number(item.total) || 0),
      backgroundColor: fuentesVisibles.map((item) => colorPorFuente[claveFuente(item)]),
      borderColor: "#fff",
      borderWidth: 2,
      hoverOffset: 6,
    }],
  }), [fuentesVisibles, colorPorFuente]);

  const sourceChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: "62%",
    plugins: {
      // La leyenda se dibuja en HTML aparte: la de Chart.js vive dentro del
      // canvas y recortaba los nombres largos contra el borde derecho.
      legend: { display: false },
      tooltip: {
        // Nativo desactivado: se dibuja dentro del canvas y recorta el texto
        // contra el borde. Lo reemplaza un tooltip HTML posicionado fuera.
        enabled: false,
        external: (context: any) => {
          const el = doughnutTooltipRef.current;
          if (!el) return;
          const { tooltip, chart } = context;
          if (!tooltip || tooltip.opacity === 0) {
            el.style.opacity = "0";
            return;
          }
          const dp = tooltip.dataPoints?.[0];
          if (dp) {
            const value = Number(dp.raw) || 0;
            const colors = chart.data.datasets?.[dp.datasetIndex]?.backgroundColor;
            const swatch = Array.isArray(colors) ? colors[dp.dataIndex] : "#0065DD";
            el.innerHTML =
              `<div style="display:flex;align-items:center;gap:7px">` +
                `<span style="width:9px;height:9px;border-radius:2px;flex-shrink:0;background:${swatch}"></span>` +
                `<span style="font-weight:700">${dp.label}</span>` +
              `</div>` +
              `<div style="margin-top:3px;color:#cbd5e1">` +
                `${value.toLocaleString("es-CL")} postulación(es) · ` +
                `<span style="color:#fff;font-weight:600">${formatearPorcentaje(porcentajeDe(value))}</span>` +
              `</div>`;
          }
          el.style.opacity = "1";
          el.style.left = `${chart.canvas.offsetLeft + tooltip.caretX}px`;
          el.style.top = `${chart.canvas.offsetTop + tooltip.caretY}px`;
        },
      },
    },
  }), [basePorcentaje]);

  const title    = isPerForm ? "Estadísticas del formulario" : "Estadísticas generales";
  const subtitle = isPerForm
    ? `Postulaciones del formulario ${contextForm?.code ?? ""}`.trim()
    : "Postulaciones de todos los formularios";

  const hasRange = !!(fromInput || toInput);
  const inputStyle: React.CSSProperties = {
    padding: "6px 10px", border: "1px solid #e2e8f0", borderRadius: "8px",
    fontFamily: MODAL_FONT, fontSize: "12.5px", color: "#334155", background: "#fff", outline: "none",
  };
  const metricLabelStyle: React.CSSProperties = {
    fontFamily: MODAL_FONT, fontSize: "10px", fontWeight: 600, color: "#94a3b8",
    textTransform: "uppercase", letterSpacing: "0.08em", whiteSpace: "nowrap",
  };
  const metricHintStyle: React.CSSProperties = {
    fontFamily: MODAL_FONT, fontSize: "10px", color: "#94a3b8", whiteSpace: "nowrap", marginTop: "1px",
  };
  const metricValueStyle: React.CSSProperties = {
    fontFamily: MODAL_FONT, fontSize: "18px", fontWeight: 700,
  };
  const labelStyle: React.CSSProperties = {
    fontFamily: MODAL_FONT, fontSize: "10px", fontWeight: 600, color: "#94a3b8",
    textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "4px", display: "block",
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      // Cierra solo al hacer clic en el fondo. No se usa stopPropagation en el
      // panel: los desplegables multiselección se cierran escuchando clics en
      // window, y cortar la propagación los dejaba abiertos para siempre.
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(5px)", WebkitBackdropFilter: "blur(5px)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}
    >
      <motion.div
        initial={{ opacity: 0, y: 28, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 18, scale: 0.97 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
        style={{ width: "100%", maxWidth: "1180px", maxHeight: "92vh", borderRadius: "18px", overflow: "hidden", background: "#fff", boxShadow: "0 28px 64px rgba(15,23,42,0.28), 0 0 0 1px rgba(15,23,42,0.07)", display: "flex", flexDirection: "column" }}
      >
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');`}</style>

        {/* ── header ── */}
        <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)", padding: "22px 24px", position: "relative", flexShrink: 0 }}>
          <div style={{ position: "absolute", top: "-20px", right: "60px", width: "120px", height: "120px", background: "radial-gradient(circle, rgba(0,101,221,0.22) 0%, transparent 70%)", pointerEvents: "none" }} />
          <button
            onClick={onClose}
            aria-label="Cerrar"
            style={{ position: "absolute", top: "14px", right: "14px", width: "30px", height: "30px", padding: 0, lineHeight: 0, borderRadius: "8px", border: "none", background: "transparent", cursor: "pointer", color: "rgba(255,255,255,0.65)", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s, color 0.15s" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.14)"; (e.currentTarget as HTMLButtonElement).style.color = "#fff"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; (e.currentTarget as HTMLButtonElement).style.color = "rgba(255,255,255,0.65)"; }}
          >
            <CloseIcon style={{ fontSize: 18, display: "block" }} />
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <div style={{ width: "48px", height: "48px", borderRadius: "13px", background: "linear-gradient(135deg, #3b82f6 0%, #0065DD 100%)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", flexShrink: 0, boxShadow: "0 4px 14px rgba(0,101,221,0.45)" }}>
              <QueryStatsIcon style={{ fontSize: 24 }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: MODAL_FONT, fontSize: "17px", fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>
                {title}
              </div>
              <div style={{ fontFamily: MODAL_FONT, fontSize: "12.5px", color: "rgba(255,255,255,0.6)", marginTop: "2px" }}>
                {subtitle}
              </div>
            </div>
          </div>
        </div>

        {/* ── body ── */}
        <div style={{ padding: "20px 24px 24px", overflowY: "auto", flex: 1 }}>
          {/* granularity selector + totals */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "14px" }}>
            <div style={{ display: "inline-flex", background: "#f1f5f9", borderRadius: "10px", padding: "3px", gap: "2px" }}>
              {GRANULARITIES.map((g) => {
                const active = g.value === granularity;
                return (
                  <button
                    key={g.value}
                    onClick={() => handleGranularityChange(g.value)}
                    style={{
                      padding: "6px 14px", borderRadius: "8px", border: "none", cursor: "pointer",
                      fontFamily: MODAL_FONT, fontSize: "12.5px", fontWeight: 600,
                      background: active ? "#fff" : "transparent",
                      color: active ? "#0065DD" : "#64748b",
                      boxShadow: active ? "0 1px 4px rgba(15,23,42,0.12)" : "none",
                      transition: "all 0.15s",
                    }}
                  >
                    {g.label}
                  </button>
                );
              })}
            </div>

            <div style={{ display: "flex", gap: "10px", alignItems: "stretch" }}>
              <div
                style={{ textAlign: "right" }}
                title={postulacionesPorPersona !== null
                  ? `Personas distintas (RUT únicos). Promedio de ${postulacionesPorPersona.toFixed(2).replace(".", ",")} postulaciones por persona.`
                  : "Personas distintas (RUT únicos) en el período."}
              >
                <div style={metricLabelStyle}>Postulantes únicos</div>
                <div style={{ ...metricValueStyle, color: "#7c3aed" }}>
                  <AnimatedNumber value={uniqueApplicants} />
                </div>
                <div style={metricHintStyle}>
                  {postulacionesPorPersona !== null ? (
                    <>
                      <AnimatedNumber
                        value={postulacionesPorPersona}
                        format={(n) => n.toFixed(2).replace(".", ",")}
                      />{" "}
                      postulaciones c/u
                    </>
                  ) : (
                    "—"
                  )}
                </div>
              </div>
              <div style={{ width: "1px", background: "#e2e8f0" }} />
              <div style={{ textAlign: "right" }}>
                <div style={metricLabelStyle}>Total postulaciones</div>
                <div style={{ ...metricValueStyle, color: "#0065DD" }}>
                  <AnimatedNumber value={totalApplications} />
                </div>
                <div style={metricHintStyle}>&nbsp;</div>
              </div>
              <div style={{ width: "1px", background: "#e2e8f0" }} />
              <div
                style={{ textAlign: "right" }}
                title={`Mayor y promedio de postulaciones por ${UNIT_LABEL[granularity].replace(/s$/, "")} en el período mostrado.`}
              >
                <div style={metricLabelStyle}>Peak postulaciones</div>
                <div style={{ ...metricValueStyle, color: "#1e293b" }}>
                  <AnimatedNumber value={peak} />
                </div>
                <div style={metricHintStyle}>
                  {promedio > 0 ? (
                    <>
                      promedio <AnimatedNumber value={promedio} />
                    </>
                  ) : (
                    "—"
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* filter bar: rango + límite */}
          <div style={{ display: "flex", alignItems: "flex-end", flexWrap: "wrap", gap: "12px", marginBottom: "16px", padding: "14px 16px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px" }}>
            {!isPerForm && (
              <div style={{ width: "240px" }}>
                <DropDownListMultipleCustomized
                  list={sourceOptions}
                  layer="Fuente de reclutamiento"
                  required={false}
                  onValueChange={setRecruitmentSourceFormVals}
                  onPermaValues={recruitmentSourceFormVals}
                  onPropertyChange="recruitmentSource"
                  placeholder="Todas las fuentes"
                />
              </div>
            )}
            {!isPerForm && (
              <div style={{ width: "240px" }}>
                <DropDownListMultipleCustomized
                  list={clientOptions}
                  layer="Cliente"
                  required={false}
                  onValueChange={setClientFormVals}
                  onPermaValues={clientFormVals}
                  onPropertyChange="client"
                  placeholder="Todos los clientes"
                />
              </div>
            )}
            {/* Con varias fuentes Y varios clientes hay que elegir qué comparar. */}
            {!isPerForm && canCompareSources && canCompareClients && (
              <div>
                <label style={labelStyle}>Comparar por</label>
                <div style={{ display: "inline-flex", background: "#e2e8f0", borderRadius: "8px", padding: "2px", gap: "2px" }}>
                  {(["recruitmentSource", "client"] as StatsBreakdown[]).map((option) => {
                    const active = option === compareBy;
                    return (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setCompareBy(option)}
                        style={{
                          padding: "5px 12px", borderRadius: "6px", border: "none", cursor: "pointer",
                          fontFamily: MODAL_FONT, fontSize: "12px", fontWeight: 600,
                          background: active ? "#fff" : "transparent",
                          color: active ? "#0065DD" : "#64748b",
                          boxShadow: active ? "0 1px 3px rgba(15,23,42,0.12)" : "none",
                        }}
                      >
                        {BREAKDOWN_LABEL[option].singular}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <label style={labelStyle}>Desde</label>
              <input
                type={RANGE_INPUT_TYPE[granularity]}
                value={fromInput}
                min={granularity === "years" ? 2000 : undefined}
                max={granularity === "years" ? 2100 : undefined}
                placeholder={granularity === "years" ? "Ej: 2020" : undefined}
                onChange={(e) => setFromInput(e.target.value)}
                style={{ ...inputStyle, width: granularity === "years" ? "110px" : "auto" }}
              />
            </div>
            <div>
              <label style={labelStyle}>Hasta</label>
              <input
                type={RANGE_INPUT_TYPE[granularity]}
                value={toInput}
                min={granularity === "years" ? 2000 : undefined}
                max={granularity === "years" ? 2100 : undefined}
                placeholder={granularity === "years" ? "Ej: 2026" : undefined}
                onChange={(e) => setToInput(e.target.value)}
                style={{ ...inputStyle, width: granularity === "years" ? "110px" : "auto" }}
              />
            </div>

            <div>
              <label style={labelStyle}>Máx. {UNIT_LABEL[granularity]} (tope {maxLimit})</label>
              <input
                type="number"
                min={1}
                max={maxLimit}
                value={limit}
                onChange={(e) => {
                  const n = Math.floor(Number(e.target.value));
                  if (!Number.isFinite(n)) return;
                  setLimit(Math.min(Math.max(1, n), maxLimit));
                }}
                style={{ ...inputStyle, width: "90px" }}
              />
            </div>

            <AnimatePresence>
              {hasRange && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
                  onClick={clearRange}
                  style={{ display: "flex", alignItems: "center", gap: "4px", padding: "7px 12px", borderRadius: "8px", border: "1px solid #fecaca", background: "#fff5f5", color: "#b91c1c", fontFamily: MODAL_FONT, fontSize: "12px", cursor: "pointer" }}
                >
                  <CloseIcon style={{ fontSize: 13 }} />
                  Limpiar rango
                </motion.button>
              )}
            </AnimatePresence>

            <span style={{ marginLeft: "auto", fontFamily: MODAL_FONT, fontSize: "11px", color: "#94a3b8", alignSelf: "center" }}>
              {RANGE_HINT[granularity]}{!hasRange ? ` · mostrando ${limit} ${UNIT_LABEL[granularity]}` : ""}
            </span>
          </div>

          {/* chart area */}
          <div style={{ position: "relative", height: "400px", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px", background: "#fff" }}>
            <AnimatePresence>
              {loading && (
                <motion.div
                  key="stats-spinner"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px", background: "rgba(255,255,255,0.7)", zIndex: 2, borderRadius: "12px" }}
                >
                  <CircularProgress size={28} thickness={4} sx={{ color: "#0065DD" }} />
                  <span style={{ fontFamily: MODAL_FONT, fontSize: "12px", color: "#64748b" }}>Cargando estadísticas…</span>
                </motion.div>
              )}
            </AnimatePresence>

            {error ? (
              <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: MODAL_FONT, fontSize: "13px", color: "#b91c1c" }}>
                {error}
              </div>
            ) : !loading && stats.length === 0 ? (
              <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: MODAL_FONT, fontSize: "13px", color: "#94a3b8" }}>
                No hay postulaciones para mostrar.
              </div>
            ) : (
              <Line data={chartData as any} options={chartOptions as any} plugins={[verticalHoverLine, compareEndLabels]} />
            )}
          </div>

          {isComparing && breakdown && (
            <div style={{ marginTop: "10px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px" }}>
              <span style={{ ...labelStyle, marginBottom: 0, marginRight: "4px" }}>
                Comparando {compareSeries.length} {BREAKDOWN_LABEL[breakdown].plural}
              </span>
              {[
                { key: COMPARE_TOTAL_KEY, label: "Total", total: totalApplications, color: COMPARE_TOTAL_COLOR },
                ...compareSeries,
              ].map((item) => {
                const oculta = seriesOcultas.includes(item.key);
                const esTotal = item.key === COMPARE_TOTAL_KEY;
                const share = !esTotal && compareSum ? (item.total / compareSum) * 100 : null;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => alternarSerie(item.key)}
                    title={oculta ? "Mostrar en el gráfico" : "Ocultar del gráfico"}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: "7px", maxWidth: "280px",
                      padding: "5px 10px", borderRadius: "999px", cursor: "pointer",
                      border: "1px solid #e2e8f0", background: oculta ? "#f8fafc" : "#fff",
                      fontFamily: MODAL_FONT, fontSize: "11.5px",
                      color: oculta ? "#cbd5e1" : "#475569",
                      textDecoration: oculta ? "line-through" : "none",
                      transition: "all 0.15s",
                    }}
                  >
                    {esTotal ? (
                      <span style={{ width: "14px", borderTop: `2px dashed ${oculta ? "#cbd5e1" : item.color}`, flexShrink: 0 }} />
                    ) : (
                      <span style={{ width: "10px", height: "10px", borderRadius: "50%", flexShrink: 0, background: item.color, opacity: oculta ? 0.35 : 1 }} />
                    )}
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: esTotal ? 700 : 500 }}>
                      {item.label}
                    </span>
                    <span style={{ fontWeight: 700, color: oculta ? "#cbd5e1" : "#1e293b", flexShrink: 0 }}>
                      <AnimatedNumber value={item.total} />
                    </span>
                    {share !== null && (
                      <span style={{ fontSize: "10px", color: oculta ? "#cbd5e1" : "#94a3b8", flexShrink: 0 }}>
                        {formatearPorcentaje(share)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <div style={{ marginTop: "8px", textAlign: "right", fontFamily: MODAL_FONT, fontSize: "11px", color: "#94a3b8" }}>
            {stats.length} {UNIT_LABEL[granularity]} con datos en pantalla
          </div>

          <div style={{ marginTop: "18px", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "18px", background: "#fff" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", marginBottom: "12px" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: MODAL_FONT, fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "2px" }}>
                  Distribución por fuente de reclutamiento
                </div>
                <div style={{ fontFamily: MODAL_FONT, fontSize: "11.5px", color: "#94a3b8" }}>
                  Considera el formulario y rango de fechas seleccionados. Haz clic en una fuente para tacharla.
                </div>
              </div>

              {/* Toggle: define si las fuentes tachadas salen o no del total. */}
              <button
                type="button"
                onClick={() => setRecalcularDistribucion((valor) => !valor)}
                title={recalcularDistribucion
                  ? "Las fuentes tachadas no se consideran en el total ni en los porcentajes"
                  : "Las fuentes tachadas se ocultan del gráfico pero siguen contando en el total"}
                style={{
                  display: "flex", alignItems: "center", gap: "8px", flexShrink: 0,
                  padding: "6px 10px", borderRadius: "9px", cursor: "pointer",
                  border: `1px solid ${recalcularDistribucion ? "#0065DD" : "#e2e8f0"}`,
                  background: recalcularDistribucion ? "#eff6ff" : "#fff",
                  fontFamily: MODAL_FONT, fontSize: "12px", fontWeight: 600,
                  color: recalcularDistribucion ? "#0065DD" : "#64748b",
                  transition: "all 0.15s",
                }}
              >
                Recalcular distribución
                <span style={{
                  position: "relative", width: "34px", height: "18px", borderRadius: "9px", flexShrink: 0,
                  background: recalcularDistribucion ? "#0065DD" : "#cbd5e1", transition: "background 0.15s",
                }}>
                  <span style={{
                    position: "absolute", top: "2px", left: recalcularDistribucion ? "18px" : "2px",
                    width: "14px", height: "14px", borderRadius: "50%", background: "#fff",
                    transition: "left 0.15s", boxShadow: "0 1px 2px rgba(15,23,42,0.25)",
                  }} />
                </span>
              </button>
            </div>

            {sourceDistribution.length === 0 ? (
              <div style={{ height: "220px", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: MODAL_FONT, fontSize: "13px", color: "#94a3b8" }}>
                No hay postulaciones para distribuir.
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: "24px", flexWrap: "wrap" }}>
                <div style={{ width: "280px", height: "280px", flexShrink: 0, position: "relative" }}>
                  {fuentesVisibles.length === 0 ? (
                    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", fontFamily: MODAL_FONT, fontSize: "12.5px", color: "#94a3b8" }}>
                      Todas las fuentes están tachadas.
                    </div>
                  ) : (
                    <Doughnut data={sourceChartData} options={sourceChartOptions} />
                  )}
                  {/* Tooltip HTML: `left/top` = posición del cursor sobre el anillo;
                      se desplaza a la derecha del punto para extenderse hacia el
                      espacio libre (leyenda) y no recortarse. */}
                  <div
                    ref={doughnutTooltipRef}
                    style={{
                      position: "absolute", top: 0, left: 0, opacity: 0,
                      transform: "translate(14px, -50%)",
                      pointerEvents: "none", transition: "opacity 0.12s ease",
                      background: "#0f172a", color: "#fff", padding: "8px 11px",
                      borderRadius: "8px", fontFamily: MODAL_FONT, fontSize: "12px",
                      lineHeight: 1.35, whiteSpace: "nowrap", zIndex: 10,
                      boxShadow: "0 6px 18px rgba(15,23,42,0.28)",
                    }}
                  />
                </div>

                {/* Leyenda en HTML: el texto fluye y se corta con "…" en vez de
                    quedar recortado contra el borde del canvas.
                    Multi-columna (no grid) para que el orden sea de arriba hacia
                    abajo y luego a la derecha: [1][2][3] / [4][5][6]. El número
                    de columnas sigue siendo responsivo según el ancho. */}
                <div style={{ flex: "1 1 340px", minWidth: "280px", columnWidth: "240px", columnGap: "18px" }}>
                  {sourceDistribution.map((item, index) => {
                    const clave = claveFuente(item);
                    const oculta = fuentesOcultas.includes(clave);
                    const valor = Number(item.total) || 0;
                    // El backend entrega la distribución ordenada por total desc,
                    // así que la posición en la lista es el ranking de la fuente.
                    const posicion = index + 1;
                    // Una fuente tachada no tiene porcentaje representable cuando
                    // quedó fuera de la base del cálculo.
                    const porcentajeOculto = oculta && recalcularDistribucion;
                    const porcentajeNum = oculta
                      ? (totalDistribucion ? (valor / totalDistribucion) * 100 : 0)
                      : porcentajeDe(valor);
                    return (
                      <button
                        key={clave}
                        type="button"
                        onClick={() => alternarFuente(clave)}
                        title={`${item.recruitmentSource} — ${valor.toLocaleString("es-CL")}`}
                        style={{
                          display: "flex", alignItems: "center", gap: "8px", width: "100%",
                          padding: "4px 6px", border: "none", borderRadius: "6px", background: "transparent",
                          cursor: "pointer", textAlign: "left", fontFamily: MODAL_FONT, fontSize: "11.5px",
                          color: oculta ? "#cbd5e1" : "#475569",
                          textDecoration: oculta ? "line-through" : "none",
                          transition: "color 0.15s, opacity 0.15s",
                          // Multi-columna: separa verticalmente y evita que una fila
                          // se parta entre dos columnas.
                          marginBottom: "3px",
                          breakInside: "avoid",
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#f8fafc"; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                      >
                        <span style={{
                          flexShrink: 0, width: "16px", textAlign: "right",
                          fontVariantNumeric: "tabular-nums", fontWeight: 700,
                          color: oculta ? "#cbd5e1" : "#94a3b8",
                        }}>
                          {posicion}
                        </span>
                        <span style={{
                          width: "10px", height: "10px", borderRadius: "3px", flexShrink: 0,
                          background: colorPorFuente[clave], opacity: oculta ? 0.35 : 1,
                        }} />
                        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {item.recruitmentSource}
                        </span>
                        {/* Conteo (dato principal) y porcentaje (secundario) se
                            diferencian por peso, color y tamaño para no confundirlos. */}
                        <span style={{ flexShrink: 0, display: "inline-flex", alignItems: "baseline", gap: "6px", whiteSpace: "nowrap" }}>
                          <span style={{ fontWeight: 700, color: oculta ? "#cbd5e1" : "#1e293b" }}>
                            <AnimatedNumber value={valor} />
                          </span>
                          <span style={{ fontWeight: 500, fontSize: "10px", color: oculta ? "#cbd5e1" : "#94a3b8" }}>
                            {porcentajeOculto ? (
                              "—"
                            ) : (
                              <>
                                <AnimatedNumber value={porcentajeNum} format={formatearPorcentaje} />
                              </>
                            )}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {recalcularDistribucion && fuentesOcultas.length > 0 && (
              <div style={{ marginTop: "12px", fontFamily: MODAL_FONT, fontSize: "11.5px", color: "#0065DD" }}>
                Porcentajes recalculados sobre {totalVisible.toLocaleString("es-CL")} postulaciones
                {" "}({sourceDistribution.length - fuentesVisibles.length} fuente(s) excluida(s) del total).
              </div>
            )}
          </div>
        </div>

        {/* ── footer ── */}
        <div style={{ padding: "14px 24px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
          <motion.button
            onClick={onClose}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            style={{ padding: "8px 22px", borderRadius: "9px", border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontFamily: MODAL_FONT, fontSize: "13px", fontWeight: 500, cursor: "pointer" }}
          >
            Cerrar
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default EstadisticasModal;
